import { useCallback, useEffect, useMemo, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useUnit } from 'effector-react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { z } from 'zod'

import ClockIcon from '@/assets/icons/icon-clock.svg'
import InfoIcon from '@/assets/icons/icon-info.svg'
import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { EmptyProvider } from '@/components/ui/empty/empty-provider.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $creatorId, $currentCreator } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { toast } from '@/models/toasts.ts'
import type { CustomTimePopupFormValues } from '@/modules/automation-settings/components/custom-time-popup.tsx'
import {
  compareTimes,
  displayTime,
  getDateWithHoursAndMinutesFromTimeObject,
  getHoursAndMinutes,
  parseTimeStringToTimObject,
} from '@/modules/creator-settings/rebill-messages/helpers/time.ts'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption.tsx'
import {
  useCreateExpiringFansMessageMutation,
  useUpdateExpiringFansMessageMutation,
} from '@/modules/expiring-fans/query/expiring-fans-query.ts'
import type {
  ExpiringFansMessage,
  ExpiringFansMessageRequestBody,
} from '@/modules/expiring-fans/types.ts'
import { ExpiringFansMessageFormSchema } from '@/modules/expiring-fans/types.ts'
import { useExpiringFanSettings } from '@/modules/expiring-fans/use-expiring-fans-settings.tsx'
import { DefaultSchemaBase } from '@/modules/message/components/editor/schema'
import { FollowUpsMessages } from '@/modules/message/components/follow-ups/follow-ups-messages.tsx'
import { MessageSequenceEditor } from '@/modules/message/components/sequence/message-sequence-editor'
import { MessageContextType } from '@/modules/message/components/sequence/types.ts'
import {
  $followUpMessages,
  $sendAfterMinutes,
  $sendIfNotSeenParentMessage,
  resetFollowUpState,
  resetSendAfterMinutesError,
  validateSendAfterMinutes,
} from '@/modules/message/models.ts'
import type { Message } from '@/modules/online-mass-messages/types.ts'
import { EmptyMessages } from '@/modules/online-mass-messages/ui/error/empty-messages'
import { ListsSelector } from '@/modules/online-mass-messages/ui/list-selector.tsx'
import { transformListsToSelectObject } from '@/modules/online-mass-messages/utils.ts'
import { AppRoutes } from '@/utils/constants.ts'

const dialogTitle = 'Add a Expiring Fans Message'
const dialogDescription = (
  <>
    Use the button to insert the fan’s name, which will replace the label with
    their actual name. If the fan doesn’t have a name, the label will be
    replaced with the fallback name.
  </>
)

const editorComponents = [
  'text',
  'media',
  'price',
  'fallback',
  'customAndFanNameLabels',
] as const

const Schema = DefaultSchemaBase.extend({ sequenceId: z.number().optional() })
const resolver = zodResolver(Schema)

interface Props {
  data?: Partial<ExpiringFansMessage>
  isEdit?: boolean
}

export const CreateExpiringFansMessagesPage = ({ data, isEdit }: Props) => {
  const redirect = useNavigate()
  const creatorId = useUnit($creatorId)
  const activeCreator = useUnit($currentCreator)
  const [isLoading, setIsLoading] = useState(false)
  const followUpMessages = useUnit($followUpMessages)
  const sendAfterMinutes = useUnit($sendAfterMinutes)
  const sendIfNotSeenParentMessage = useUnit($sendIfNotSeenParentMessage)
  const { messages: expiringFansMessages } = useExpiringFanSettings()

  useEffect(() => {
    return () => resetFollowUpState()
  }, [])

  const form = useForm({
    defaultValues: {
      excludeList: data?.excludedFanLists || [],
      includeList: data?.includedFanLists || [],
      fromTime: getDateWithHoursAndMinutesFromTimeObject(
        parseTimeStringToTimObject(data?.timeframeStart || ''),
      ),
      toTime: getDateWithHoursAndMinutesFromTimeObject(
        parseTimeStringToTimObject(data?.timeframeEnd || ''),
      ),
      messages: data?.sequence?.messages || [],
      followUpEnabled: data?.followUpEnabled || false,
      withoutTimeframe: isEdit && !data?.timeframeStart && !data?.timeframeEnd,
    },
    resolver: zodResolver(ExpiringFansMessageFormSchema),
  })

  const [
    excludedFanLists,
    includedFanLists,
    messages,
    fromTime,
    toTime,
    followUpEnabled,
    withoutTimeframe,
  ] = useWatch({
    name: [
      'excludeList',
      'includeList',
      'messages',
      'fromTime',
      'toTime',
      'followUpEnabled',
      'withoutTimeframe',
    ],
    control: form.control,
  })

  const createExpiringFansMessageMutation =
    useCreateExpiringFansMessageMutation()
  const updateExpiringFansMessageMutation =
    useUpdateExpiringFansMessageMutation()

  const openTimeSelectDialog = () => {
    openDialog({
      type: DIALOG_TYPE.SELECT_TIME_POPUP,
      title: 'Timeframe',
      fromTime,
      toTime,
      id: data?.id,
      update: handleCustomTimePopupUpdate,
      bookedIntervals: bookedIntervals,
    })
  }

  const handleWithoutTimeframe = () => {
    form.setValue('withoutTimeframe', !withoutTimeframe)
  }

  const handleSubmitEnd = () => {
    if (!creatorId) {
      return
    }
    resetFollowUpState()
    redirect(AppRoutes.Creator.Settings.ExpiringFans.asUrl(creatorId))
  }

  const validateForm = () => {
    validateSendAfterMinutes()
    toast.error('Please fill required fields')
  }

  const createMessage = async () => {
    if (followUpEnabled && !sendAfterMinutes) {
      validateForm()

      return
    }

    setIsLoading(true)
    const values = form.getValues()
    const submitData: ExpiringFansMessageRequestBody = {
      id: data?.id,
      messages: values.messages,
      timeframeStart: values.withoutTimeframe
        ? null
        : getHoursAndMinutes(values.fromTime),
      timeframeEnd: values.withoutTimeframe
        ? null
        : getHoursAndMinutes(values.toTime),
      includedFanLists: values.includeList,
      excludedFanLists: values.excludeList,
      followUpEnabled: values.followUpEnabled,
      followUpDto: {
        messages: followUpMessages,
        sendAfterMinutes,
        sendIfNotSeenParentMessage,
      },
    }

    if (isEdit) {
      await updateExpiringFansMessageMutation.mutateAsync(submitData)
      handleSubmitEnd()
      setIsLoading(false)

      return
    }

    await createExpiringFansMessageMutation.mutateAsync(submitData)
    setIsLoading(false)
    handleSubmitEnd()
  }

  const handleSaveLocalMessage = useCallback(
    async (message: Message) => {
      const data = {
        ...message,
        id: messages.length,
      }

      form.setValue('messages', [...messages, data])
    },
    [form, messages],
  )

  const handleFollowUpEnable = useCallback(() => {
    resetSendAfterMinutesError()
    form.setValue('followUpEnabled', !followUpEnabled)
  }, [followUpEnabled, form])

  const handleUpdateLocalMessage = useCallback(
    async (message: Message) => {
      const newMessages = messages.map((m) => {
        return m.id === message.id ? message : m
      })

      form.setValue('messages', newMessages)
    },
    [form, messages],
  )

  const handleRemoveMessage = useCallback(
    async ({ id }: { id: number }) => {
      const copyMessages = messages.filter((m) => {
        return m.id !== id
      })

      form.setValue('messages', copyMessages)
    },
    [form, messages],
  )

  const handleCustomTimePopupUpdate = useCallback(
    ({ fromTime, toTime }: CustomTimePopupFormValues) => {
      form.setValue('fromTime', fromTime)
      form.setValue('toTime', toTime)
    },
    [form],
  )

  const bookedIntervals = useMemo(() => {
    return expiringFansMessages
      .filter((m) => m.id !== data?.id && m.timeframeStart && m.timeframeEnd)
      .sort((a, b) => compareTimes(a.timeframeStart, b.timeframeStart))
      .map((message) => ({
        start: message.timeframeStart,
        end: message.timeframeEnd,
      }))
  }, [data?.id, expiringFansMessages])

  const listOptions = transformListsToSelectObject(activeCreator?.lists)

  const isDefaultMessageExist = useMemo(() => {
    const defaultMessages = expiringFansMessages.find(
      (m) => !m.timeframeStart && !m.timeframeEnd,
    )

    if (!defaultMessages) {
      return false
    }

    return defaultMessages && defaultMessages.id !== data?.id
  }, [data?.id, expiringFansMessages])

  const disableSave =
    !messages.length ||
    (followUpEnabled && !(followUpMessages.length && sendAfterMinutes))

  return (
    <Page
      title={(isEdit ? 'Edit' : 'Create') + ' Expiring Fans Messages'}
      description="Configure your Expiring Fans Message to be sent out to fans who soon expire during the selected time frame."
      backToPage={{
        name: 'Expiring Fans Messages',
        url: AppRoutes.Creator.Settings.ExpiringFans.toString(),
      }}
    >
      <FormProvider {...form}>
        <div>
          <Separator />

          <div className="text-title flex items-center gap-2 py-8 font-medium">
            <Checkbox
              checked={withoutTimeframe}
              onClick={handleWithoutTimeframe}
              disabled={isDefaultMessageExist}
            />
            Create a message without timeframe{' '}
            <Tooltip
              side="right"
              sideOffset={20}
              content={
                isDefaultMessageExist
                  ? 'Default message already exist'
                  : 'If message for this time not exist this message will be as default'
              }
            >
              <InfoIcon className="text-secondary-300 h-4 w-4" />
            </Tooltip>
          </div>

          {!withoutTimeframe && (
            <>
              <SettingsOption
                title="Timeframe"
                description="The timeframe in which this message should be sent out to a fan."
                mobileVertical
                biggerTitle
              >
                <div
                  className="border-secondary-300 bg-card text-secondary-300 hover:border-secondary-100 flex cursor-pointer  select-none items-center gap-2 rounded-md border-2 px-3 py-2"
                  onClick={openTimeSelectDialog}
                >
                  <ClockIcon />
                  <span>{displayTime(fromTime!)}</span>
                  {'- '}
                  {displayTime(toTime!)}
                </div>
              </SettingsOption>
              <Separator />
            </>
          )}

          <div className="flex flex-wrap justify-between">
            <SettingsOption
              title="Fan Lists Control"
              description="Include or exclude fans from specific lists for this Rebill Messages"
              mobileVertical
              biggerTitle
            />
            <div className="flex flex-col gap-10 py-6 md:flex-row">
              <ListsSelector
                label="Only include the selected Fan Lists"
                name="includeList"
                placeholder="Select Lists"
                lists={listOptions}
                backgroundCard
                excludeValues={excludedFanLists}
              />

              <ListsSelector
                label="Exclude the selected Fan Lists"
                placeholder="Select Lists"
                name="excludeList"
                lists={listOptions}
                backgroundCard
                excludeValues={includedFanLists}
              />
            </div>
          </div>
          <Separator />

          <EmptyProvider render={() => <EmptyMessages action="Add Message" />}>
            <MessageSequenceEditor
              title="Messages"
              description="Configure multiple messages that will be sent to your fans"
              placeholder="Hey %name%, how are you?"
              addMessageLabel="Add Message"
              dialogTitle={dialogTitle}
              dialogDescription={dialogDescription}
              components={editorComponents}
              schema={resolver}
              entries={messages}
              onCreate={handleSaveLocalMessage}
              onUpdate={handleUpdateLocalMessage}
              onRemove={handleRemoveMessage}
              type={MessageContextType.FollowUpMessage}
            />
          </EmptyProvider>

          <div className="pt-[32px]">
            <FollowUpsMessages
              type="Expiring Fans Message"
              dialogTitle="Add Follow Up Expiring Fans Message"
              enabled={followUpEnabled}
              toggleChange={handleFollowUpEnable}
              data={data?.followUpDto}
            />
          </div>

          <div className="mt-8 flex justify-end">
            <Button
              disabled={disableSave}
              onClick={createMessage}
              isLoading={isLoading}
            >
              {isEdit ? (
                'Update'
              ) : (
                <>
                  <PlusIcon /> Create{' '}
                </>
              )}{' '}
              Expiring Fans Message
            </Button>
          </div>
        </div>
      </FormProvider>
    </Page>
  )
}
